import styled from 'styled-components';
import themes from 'constants/Colors';

/**
 * Periodväljare som delas av flikarna under Kvalitet (BETA): snabbval 7/30/90 dagar
 * plus fria från/till-datum. Datumen hålls som yyyy-mm-dd-strängar i lokal tid och
 * konverteras till start/slut på dygnet med startOfDay/endOfDay innan de skickas
 * till backend.
 */

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  background-color: #f8f9fa;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
`;

export const PresetButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? '#17a2b8' : '#ccc')};
  background-color: ${({ active }) => (active ? '#17a2b820' : '#fff')};
  color: ${({ active }) => (active ? '#17a2b8' : '#333')};
  cursor: pointer;
  font-size: 13px;
`;

export const DateInput = styled.input`
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
`;

export const FilterSelect = styled.select`
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  background-color: #fff;
`;

export const formatDateInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const startOfDay = (yyyymmdd: string): Date => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

export const endOfDay = (yyyymmdd: string): Date => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
};

export type DateRange = { fromYmd: string; toYmd: string };

export const presetForDays = (days: number): DateRange => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { fromYmd: formatDateInput(from), toYmd: formatDateInput(to) };
};

const DateRangeFilter = ({
  range,
  onChange,
  children,
}: {
  range: DateRange;
  onChange: (r: DateRange) => void;
  /** Extra filter som ska ligga på samma rad som periodväljaren. */
  children?: React.ReactNode;
}) => {
  const presets = [
    { label: '7 dagar', days: 7 },
    { label: '30 dagar', days: 30 },
    { label: '90 dagar', days: 90 },
  ];
  const activePreset = presets.find((p) => {
    const candidate = presetForDays(p.days);
    return (
      candidate.fromYmd === range.fromYmd && candidate.toYmd === range.toYmd
    );
  });

  return (
    <FilterBar>
      <strong style={{ fontSize: '13px' }}>Period:</strong>
      {presets.map((p) => (
        <PresetButton
          key={p.days}
          active={activePreset?.days === p.days}
          onClick={() => onChange(presetForDays(p.days))}
        >
          {p.label}
        </PresetButton>
      ))}
      <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666' }}>
        Från:
      </span>
      <DateInput
        type="date"
        value={range.fromYmd}
        max={range.toYmd}
        onChange={(e) => onChange({ ...range, fromYmd: e.target.value })}
      />
      <span style={{ fontSize: '13px', color: '#666' }}>Till:</span>
      <DateInput
        type="date"
        value={range.toYmd}
        min={range.fromYmd}
        onChange={(e) => onChange({ ...range, toYmd: e.target.value })}
      />
      {children}
    </FilterBar>
  );
};

export default DateRangeFilter;
